import { nullCheck } from "../utils/dataHelpers";
import { parseMcleodDate, formatDateForMcleod } from "../utils/dateHelpers";

export default class CarrierSerializer {
  static serialize(data) {
    const payload = {
      ...data,
      name: data.name,
      id: data.id,
      phone_umber: data.phoneNumber,
      email: data.email,
      address1: data.address1,
      address2: data.address2,
      city: data.city,
      state: data.state,
      zip_code: data.zipCode,
      drsPayee: {
        ...data.drsPayee,
        icc_number: data.mcNumber,
        dot_number: data.dotNumber,
        safety_rating: data.safetyRating,
        online_track_password: data.onlineTrackPassword,
        online_track_url: data.onlineTrackUrl,
        online_track_user_name: data.onlineTrackUsername,
        cargo_ins_amount: parseInt(data.cargoAmount),
        cargo_ins_amount_n: parseInt(data.cargoAmount),
        cargo_ins_renew_dt: formatDateForMcleod(data.cargoExpiration),
        cargo_policy: data.cargoPolicy,
        liability_amount: parseInt(data.liabilityAmount),
        liability_amount_n: parseInt(data.liabilityAmount),
        liab_expire_date: formatDateForMcleod(data.liabilityExpiration),
        liability_policy: data.liabilityPolicy,
        power_units: data.powerUnits,
        leased_trailers: data.leasedTrailers,
        leased_tractors: data.leasedTractors,
        owned_trailers: data.ownedTrailers,
        owned_tractors: data.ownedTractors,
        eld: data.eld,
        eq_op_bulkhead: data.trailerAccessories.bulkhead,
        eq_op_container_flat: data.trailerAccessories.containerOnFlat,
        eq_op_double_drop: data.trailerAccessories.doubleDrop,
        eq_op_downloadable_tru: data.trailerAccessories.downloadable,
        eq_op_food_grade: data.trailerAccessories.foodGrade,
        eq_op_heated: data.trailerAccessories.heated,
        eq_op_high_cube: data.trailerAccessories.highCube,
        eq_op_lift_gate: data.trailerAccessories.liftGate,
        eq_op_refrig_straight: data.trailerAccessories.reeferStraightTruck,
        eq_op_rgn: data.trailerAccessories.rgn,
        eq_op_roll_up: data.trailerAccessories.rollUp,
        eq_op_standard_height: data.trailerAccessories.standardHeight,
        eq_op_step_deck: data.trailerAccessories.stepDeck,
        eq_op_swing_doors: data.trailerAccessories.swingDoors,
        eq_op_trailer_flat: data.trailerAccessories.trailerOnFlat,
        eq_op_vent_trailer: data.trailerAccessories.ventedTrailer,
        eq_op_wood_floor: data.trailerAccessories.woodFloor,
        plated_trailers_yes: data.trailerAccessories.plated,
        warehouse: data.services.warehouse,
        drop_trailer_yes: data.services.dropTrailer,
        evening_pu_del: data.services.eveningPickup,
        hazmat_yes: data.services.hazmat,
        ltl: data.services.ltl,
        intermodal: data.services.intermodal,
        private_fleet: data.services.privateFleet,
        power_only_yes: data.services.powerOnly,
        drayage: data.services.drayage,
        teams_yes: data.services.teams,
        cargo_agri_farm: data.cargoTypes.agriculturalSupplies,
        cargo_beer: data.cargoTypes.beer,
        cargo_beverages: data.cargoTypes.beverages,
        cargo_building_mat: data.cargoTypes.buildingMaterials,
        cargo_chemicals: data.cargoTypes.chemicals,
        cargo_coal_coke: data.cargoTypes.coal,
        cargo_commod_dry: data.cargoTypes.commoditiesDryBulk,
        cargo_construction: data.cargoTypes.construction,
        cargo_drive_tow: data.cargoTypes.driveTow,
        cargo_electronics: data.cargoTypes.electronics,
        cargo_floor_loaded: data.cargoTypes.floorLoaded,
        cargo_garbage: data.cargoTypes.garbage,
        cargo_gen_freight: data.cargoTypes.generalFreight,
        cargo_grain_feed: data.cargoTypes.grainFeed,
        cargo_household: data.cargoTypes.householdGoods,
        cargo_ice_cream: data.cargoTypes.iceCream,
        cargo_intermodal: data.cargoTypes.intermodalCargo,
        cargo_liquids_gas: data.cargoTypes.liquidOrGas,
        cargo_liquor: data.cargoTypes.liquor,
        cargo_livestock: data.cargoTypes.livestock,
        cargo_logs_lumber: data.cargoTypes.lumber,
        cargo_machinery: data.cargoTypes.machinery,
        cargo_meat: data.cargoTypes.meat,
        cargo_metal: data.cargoTypes.metal,
        cargo_mobile_homes: data.cargoTypes.mobileHomes,
        cargo_oil_equip: data.cargoTypes.oilFieldEquipment,
        cargo_other: data.cargoTypes.other,
        cargo_paper_prod: data.cargoTypes.paperProducts,
        cargo_paper_rolls: data.cargoTypes.paperRolls,
        cargo_passengers: data.cargoTypes.passengers,
        cargo_pork: data.cargoTypes.pork,
        cargo_produce: data.cargoTypes.produce,
        cargo_reefer_food: data.cargoTypes.reeferFood,
        cargo_seafood: data.cargoTypes.seafood,
        cargo_tobacco: data.cargoTypes.tobacco,
        cargo_us_mail: data.cargoTypes.usMail,
        cargo_utilities: data.cargoTypes.utilities,
        cargo_vehicles: data.cargoTypes.vehicles,
        cargo_water_well: data.cargoTypes.water,
        pay_advances: data.payAdvances,
        carrier_mgr: data.carrierManager,
        pipeline_status: data.pipelineStatus,
        no_dispatch: data.noDispatch
      }
    };

    const customProperties = [
      "payAdvances",
      "carrierManager",
      "pipelineStatus",
      "noDispatch"
    ];

    customProperties.forEach(prop => delete payload[prop]);
    return payload;
  }

  static deSerialize(data) {
    return {
      name: nullCheck(data, null, "name"),
      id: data.id,
      mcNumber: nullCheck(data, null, "drsPayee", "icc_number"),
      dotNumber: nullCheck(data, null, "drsPayee", "dot_number"),
      phoneNumber: data.phone_number,
      email: data.email,
      address1: data.address1,
      address2: data.address2,
      city: data.city,
      state: data.state,
      cityState: `${data.city}, ${data.state}`,
      zipCode: data.zip_code,
      safetyRating: nullCheck(data, null, "drsPayee", "safety_rating"),
      onlineTrackPassword: nullCheck(data, null, "drsPayee", "online_track_password"),
      onlineTrackUrl: nullCheck(data, null, "drsPayee", "online_track_url"),
      onlineTrackUserName: nullCheck(data, null, "drsPayee", "online_track_username"),
      cargoAmount: nullCheck(data, "", "drsPayee", "cargo_ins_amount"),
      cargoExpiration: parseMcleodDate(nullCheck(data, null, "drsPayee", "cargo_ins_renew_dt")),
      cargoPolicy: nullCheck(data, "", "drsPayee", "cargo_policy"),
      liabilityAmount: nullCheck(data, "", "drsPayee", "liability_amount"),
      liabilityExpiration: parseMcleodDate(nullCheck(data, null, "drsPayee", "liab_expire_date")),
      liabilityPolicy: nullCheck(data, "", "drsPayee", "liability_policy"),
      powerUnits: nullCheck(data, null, "drsPayee", "power_units"),
      leasedTrailers: nullCheck(data, null, "drsPayee", "leased_trailers"),
      leasedTractors: nullCheck(data, null, "drsPayee", "leased_tractors"),
      ownedTrailers: nullCheck(data, null, "drsPayee", "owned_trailers"),
      ownedTractors: nullCheck(data, null, "drsPayee", "owned_tractors"),
      payAdvances: nullCheck(data, null, "drsPayee", "pay_advances"),
      carrierManager: nullCheck(data, null, "drsPayee", "carrier_mgr"),
      pipelineStatus: nullCheck(data, null, "drsPayee", "pipeline_status"),
      noDispatch: nullCheck(data, null, "drsPayee", "no_dispatch"),
      trailerAccessories: {
        bulkhead: nullCheck(data, null, "drsPayee", "eq_op_bulkhead"),
        containerOnFlat: nullCheck(data, null, "drsPayee", "eq_op_container_flat"),
        doubleDrop: nullCheck(data, null, "drsPayee", "eq_op_double_drop"),
        downloadable: nullCheck(data, null, "drsPayee", "eq_op_downloadable_tru"),
        foodGrade: nullCheck(data, null, "drsPayee", "eq_op_food_grade"),
        heated: nullCheck(data, null, "drsPayee", "eq_op_heated"),
        highCube: nullCheck(data, null, "drsPayee", "eq_op_high_cube"),
        liftGate: nullCheck(data, null, "drsPayee", "eq_op_lift_gate"),
        reeferStraightTruck: nullCheck(data, null, "drsPayee", "eq_op_refrig_straight"),
        rgn: nullCheck(data, null, "drsPayee", "eq_op_rgn"),
        rollUp: nullCheck(data, null, "drsPayee", "eq_op_roll_up"),
        standardHeight: nullCheck(data, null, "drsPayee", "eq_op_standard_height"),
        stepDeck: nullCheck(data, null, "drsPayee", "eq_op_step_deck"),
        swingDoors: nullCheck(data, null, "drsPayee", "eq_op_swing_doors"),
        trailerOnFlat: nullCheck(data, null, "drsPayee", "eq_op_trailer_flat"),
        ventedTrailer: nullCheck(data, null, "drsPayee", "eq_op_vent_trailer"),
        woodFloor: nullCheck(data, null, "drsPayee", "eq_op_wood_floor"),
        plated: nullCheck(data, null, "drsPayee", "plated_trailers_yes")
      },
      services: {
        drayage: nullCheck(data, null, "drsPayee", "drayage"),
        dropTrailer: nullCheck(data, null, "drsPayee", "drop_trailer_yes"),
        eveningPickup: nullCheck(data, null, "drsPayee", "evening_pu_del"),
        hazmat: nullCheck(data, null, "drsPayee", "hazmat_yes"),
        intermodal: nullCheck(data, null, "drsPayee", "intermodal"),
        ltl: nullCheck(data, null, "drsPayee", "ltl"),
        powerOnly: nullCheck(data, null, "drsPayee", "power_only_yes"),
        privateFleet: nullCheck(data, null, "drsPayee", "private_fleet"),
        teams: nullCheck(data, null, "drsPayee", "teams_yes"),
        warehouse: nullCheck(data, null, "drsPayee", "warehouse"),
      },
      cargoTypes: {
        agriculturalSupplies: nullCheck(data, null, "drsPayee", "cargo_agri_farm"),
        beer: nullCheck(data, null, "drsPayee", "cargo_beer"),
        beverages: nullCheck(data, null, "drsPayee", "cargo_beverages"),
        buildingMaterials: nullCheck(data, null, "drsPayee", "cargo_building_mat"),
        chemicals: nullCheck(data, null, "drsPayee", "cargo_chemicals"),
        coal: nullCheck(data, null, "drsPayee", "cargo_coal_coke"),
        commoditiesDryBulk: nullCheck(data, null, "drsPayee", "cargo_commod_dry"),
        construction: nullCheck(data, null, "drsPayee", "cargo_construction"),
        driveTow: nullCheck(data, null, "drsPayee", "cargo_drive_tow"),
        electronics: nullCheck(data, null, "drsPayee", "cargo_electronics"),
        floorLoaded: nullCheck(data, null, "drsPayee", "cargo_floor_loaded"),
        garbage: nullCheck(data, null, "drsPayee", "cargo_garbage"),
        grainFeed: nullCheck(data, null, "drsPayee", "cargo_grain_feed"),
        generalFreight: nullCheck(data, null, "drsPayee", "cargo_gen_freight"),
        householdGoods: nullCheck(data, null, "drsPayee", "cargo_household"),
        iceCream: nullCheck(data, null, "drsPayee", "cargo_ice_cream"),
        intermodalCargo: nullCheck(data, null, "drsPayee", "cargo_intermodal"),
        liquidOrGas: nullCheck(data, null, "drsPayee", "cargo_liquids_gas"),
        liquor: nullCheck(data, null, "drsPayee", "cargo_liquor"),
        livestock: nullCheck(data, null, "drsPayee", "cargo_livestock"),
        lumber: nullCheck(data, null, "drsPayee", "cargo_logs_lumber"),
        machinery: nullCheck(data, null, "drsPayee", "cargo_machinery"),
        meat: nullCheck(data, null, "drsPayee", "cargo_meat"),
        metal: nullCheck(data, null, "drsPayee", "cargo_metal"),
        mobileHomes: nullCheck(data, null, "drsPayee", "cargo_mobile_homes"),
        oilFieldEquipment: nullCheck(data, null, "drsPayee", "cargo_oil_equip"),
        other: nullCheck(data, null, "drsPayee", "cargo_other"),
        paperProducts: nullCheck(data, null, "drsPayee", "cargo_paper_prod"),
        paperRolls: nullCheck(data, null, "drsPayee", "cargo_paper_rolls"),
        passengers: nullCheck(data, null, "drsPayee", "cargo_passengers"),
        pork: nullCheck(data, null, "drsPayee", "cargo_pork"),
        produce: nullCheck(data, null, "drsPayee", "cargo_produce"),
        reeferFood: nullCheck(data, null, "drsPayee", "cargo_reefer_food"),
        seafood: nullCheck(data, null, "drsPayee", "cargo_seafood"),
        tobacco: nullCheck(data, null, "drsPayee", "cargo_tobacco"),
        usMail: nullCheck(data, null, "drsPayee", "cargo_us_mail"),
        utilities: nullCheck(data, null, "drsPayee", "cargo_utilities"),
        vehicles: nullCheck(data, null, "drsPayee", "cargo_vehicles"),
        water: nullCheck(data, null, "drsPayee", "cargo_water_well")
      },
      ...data
    };
  }
}